import { CONFIG_CHAR_LIMITS } from "./adminConfigLimits";

export interface ConfigField {
  key: string;
  label: string;
  apiEndpoint: string;
  // "recipients" → leave CC editor; "uploadConfig" → max files + max file size
  // editor (api/upload-config/{MODULE}). Omitted → generic name-list CRUD.
  variant?: "recipients" | "uploadConfig";
  // Some endpoints expect the UPDATE body as { names: [value] } instead of the
  // default { name: value } (e.g. the employee config endpoints). Opt in here.
  updateWithNamesArray?: boolean;
  // Short helper text shown under the field name on the config cards.
  description?: string;
  // Max characters allowed for a value — sourced from CONFIG_CHAR_LIMITS so the
  // UI validation matches the backend. Enforced by the config editors.
  maxLength?: number;
}

export interface ConfigSection {
  key: string;
  label: string;
  fields: ConfigField[];
  // Short helper text shown under the section title on the landing page.
  description?: string;
}

export const CONFIG_SECTIONS: ConfigSection[] = [
  {
    key: "reimbursement",
    label: "Reimbursement",
    description: "Categories and rules employees claim expenses against.",
    fields: [
      {
        key: "reimbursementType",
        label: "Reimbursement Type",
        apiEndpoint: "api/reimbursement-types",
        updateWithNamesArray: true,
        description: "Expense categories available when raising a claim.",
        maxLength: CONFIG_CHAR_LIMITS.REIMBURSEMENT_TYPE,
      },
      {
        key: "reimbursementCurrencyType",
        label: "Currency Type",
        apiEndpoint: "api/reimbursement-currency-types",
        updateWithNamesArray: true,
        description: "Currencies employees can claim reimbursements in.",
        maxLength: CONFIG_CHAR_LIMITS.CURRENCY,
      },
      // Allowed attachment formats for reimbursement receipts.
      {
        key: "reimbursementFileType",
        label: "File Type",
        apiEndpoint: "api/reimbursement-file-types",
        updateWithNamesArray: true,
        description: "File formats allowed when attaching reimbursement receipts.",
        maxLength: CONFIG_CHAR_LIMITS.REIMBURSEMENT_FILE_TYPE,
      },
      // Upload limits (max files + max file size) for reimbursement attachments.
      {
        key: "reimbursementFileSize",
        label: "File Size",
        apiEndpoint: "api/upload-config/REIMBURSEMENT",
        variant: "uploadConfig",
        description: "Maximum number and size of reimbursement attachments.",
      },
    ],
  },

  {
    key: "leave",
    label: "Leave Recipient",
    description: "Who is notified when employees request leave.",
    fields: [
      {
        key: "leaveRecipient",
        label: "Leave Recipient",
        apiEndpoint: "api/leave-mail-cc-config",
        variant: "recipients",
        description: "Admin emails CC'd on every leave notification.",
      },
    ],
  },
  // Leave Policy — rich manager UI (no generic name-list fields). Rendered
  // specially in AdminConfig's index, like the Liri Admin section.
  {
    key: "leavePolicy",
    label: "Leave Policy",
    description: "Define leave types, balances and the policy year.",
    fields: [],
  },
  // Quarterly Feedback — KRA areas and their objectives. Two levels deep with
  // an active toggle per level, so it gets its own editor rather than the
  // generic name-list one (see components/KraConfig.tsx).
  {
    key: "feedbackKra",
    label: "Feedback KRA",
    description: "Areas and objectives employees pick from on their goal sheet.",
    fields: [],
  },
  {
    key: "employee",
    label: "Employee",
    description: "Classifications and job titles used across employees.",
    fields: [
      // Employee endpoints live under api/employee/* and expect { names: [...] }
      // for both create and update, so updateWithNamesArray is set.
      {
        key: "employeeType",
        label: "Employee Type",
        apiEndpoint: "api/employee/employee-types",
        updateWithNamesArray: true,
        description: "Employment classifications (e.g. Permanent, Contract).",
        maxLength: CONFIG_CHAR_LIMITS.EMPLOYEE_TYPE,
      },
      {
        key: "designation",
        label: "Designation",
        apiEndpoint: "api/employee/designations",
        updateWithNamesArray: true,
        description: "Job titles assignable to employees.",
        maxLength: CONFIG_CHAR_LIMITS.DESIGNATION,
      },
      // Allowed formats for employee document uploads.
      {
        key: "documentFileType",
        label: "Document File Type",
        apiEndpoint: "api/employee/document-file-types",
        updateWithNamesArray: true,
        description: "File formats allowed when uploading employee documents.",
        maxLength: CONFIG_CHAR_LIMITS.DOCUMENT_FILE_TYPE,
      },
      // Upload limits (max files + max file size) for employee document uploads.
      {
        key: "documentFileSize",
        label: "Document File Size",
        apiEndpoint: "api/upload-config/DOCUMENT",
        variant: "uploadConfig",
        description: "Maximum number and size of employee document uploads.",
      },
    ],
  },
  {
    key: "asset",
    label: "Asset",
    description: "Inventory asset, device and status master data.",
    fields: [
      // Inventory endpoints live under api/inventory/* and expect { names: [...] }
      // for both create and update, so updateWithNamesArray is set.
      {
        key: "assetType",
        label: "Asset Type",
        apiEndpoint: "api/inventory/asset-types",
        updateWithNamesArray: true,
        description: "Ownership classes for assets (e.g. Own, Rental).",
        maxLength: CONFIG_CHAR_LIMITS.ASSET_TYPE,
      },
      {
        key: "deviceType",
        label: "Device Type",
        apiEndpoint: "api/inventory/device-types",
        updateWithNamesArray: true,
        description: "Kinds of device/equipment tracked in inventory.",
        maxLength: CONFIG_CHAR_LIMITS.DEVICE_TYPE,
      },
      {
        key: "deviceStatus",
        label: "Device Status",
        apiEndpoint: "api/inventory/device-statuses",
        updateWithNamesArray: true,
        description: "Lifecycle states a device can be in.",
        maxLength: CONFIG_CHAR_LIMITS.DEVICE_STATUS,
      },
      // Allowed formats for inventory/asset file uploads.
      {
        key: "assetFileType",
        label: "File Type",
        apiEndpoint: "api/inventory/file-types",
        updateWithNamesArray: true,
        description: "File formats allowed when attaching inventory/asset files.",
        maxLength: CONFIG_CHAR_LIMITS.ASSET_FILE_TYPE,
      },
      // Upload limits (max files + max file size) for inventory/asset uploads.
      {
        key: "assetFileSize",
        label: "File Size",
        apiEndpoint: "api/upload-config/ASSET",
        variant: "uploadConfig",
        description: "Maximum number and size of inventory/asset file uploads.",
      },
    ],
  },
  // NEW: Liri Admin section
  {
    key: "liriadmin",
    label: "Liri Admin",
    description: "Platform build and release management.",
    fields: [],
  },
];